import { useState, useEffect } from 'react'
import { useDebug } from '../contexts/DebugContext'
import { DEBUG_INFO, isLocalhost, type DebugConfig } from '../config/debug'
import { sectionsConfig } from '../config/sections'
import { projectsConfig } from '../config/projects'
import './DebugPanel.css'

type GroupSection = {
  title: string
  rows: { label: string; key: string; type: 'boolean' | 'choice'; choices?: { label: string; value: number }[] }[]
}

// Key → ORDER ID mapping for drag reorder
// WHY MODULE SCOPE: Built once at import time by iterating over config arrays.
// If config arrays change dynamically (unlikely), maps would be stale. Safe for
// static config.
const projectKeyToId: Record<string, string> = {}
projectsConfig.forEach(p => { projectKeyToId[p.debugKey] = p.id })

const sectionKeyToId: Record<string, string> = {}
sectionsConfig.filter(s => s.id !== 'footer').forEach(s => { sectionKeyToId[s.id.toUpperCase()] = s.id })

const DebugPanel = () => {
  const { config, unlocked, setOverride, commitOverrides, resetAll, productionMode, setProductionMode } = useDebug()
  const [open, setOpen] = useState(false)
  const [tooltipKey, setTooltipKey] = useState<string | null>(null)
  const [committed, setCommitted] = useState(false)
  const APPLY_PENDING_KEY = 'ma_apply_source_pending'
  const [rowDrag, setRowDrag] = useState<{ group: string; index: number } | null>(null)
  const [rowDraft, setRowDraft] = useState<Record<string, string[]>>({})
  const [filterText, setFilterText] = useState('')
  const [confirmReset, setConfirmReset] = useState(false)
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number; key: string } | null>(null)
  const [touchDrag, setTouchDrag] = useState<{ group: string; index: number } | null>(null)

  // WHY mousedown (not click): Same pattern as Navbar. Prevents tooltip from closing
  // when clicking interactive elements within it.
  useEffect(() => {
    if (!tooltipKey) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (!(target as Element)?.closest?.('.debug-info-btn, .debug-tooltip-fixed')) {
        setTooltipKey(null)
        setTooltipPos(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [tooltipKey])

  const getSortedKeys = (
    keys: string[],
    order: string[] | null,
    idMap: Record<string, string>,
    draft: string[] | null,
  ): string[] => {
    if (draft) {
      const draftIndex = new Map(draft.map((id, i) => [id, i]))
      return [...keys].sort((a, b) => {
        const ai = draftIndex.get(idMap[a] ?? '')
        const bi = draftIndex.get(idMap[b] ?? '')
        if (ai !== undefined && bi !== undefined) return ai - bi
        if (ai !== undefined) return -1
        if (bi !== undefined) return 1
        return 0
      })
    }
    if (order && order.length > 0) {
      const orderMap = new Map(order.map((id, i) => [id, i]))
      return [...keys].sort((a, b) => {
        const ai = orderMap.get(idMap[a] ?? '')
        const bi = orderMap.get(idMap[b] ?? '')
        if (ai !== undefined && bi !== undefined) return ai - bi
        if (ai !== undefined) return -1
        if (bi !== undefined) return 1
        return 0
      })
    }
    return [...keys]
  }

  // WHY SECURITY GATE: Entire panel returns null if not localhost or not unlocked.
  // This is the ONLY security gate. Debug panel contains full config manipulation UI.
  if (!isLocalhost() || !unlocked) return null

  const groups: GroupSection[] = [
    {
      title: 'Projects',
      rows: getSortedKeys(
        Object.keys(config.PROJECTS),
        config.ORDER?.PROJECTS ?? null,
        projectKeyToId,
        rowDrag?.group === 'Projects' ? rowDraft['Projects'] ?? null : null,
      ).map((key) => ({
        label: key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
        key: `PROJECTS.${key}`,
        type: 'boolean' as const,
      })),
    },
    {
      title: 'Sections',
      rows: getSortedKeys(
        Object.keys(config.SECTIONS),
        config.ORDER?.SECTIONS ?? null,
        sectionKeyToId,
        rowDrag?.group === 'Sections' ? rowDraft['Sections'] ?? null : null,
      ).map((key) => ({
        label: key.charAt(0) + key.slice(1).toLowerCase(),
        key: `SECTIONS.${key}`,
        type: 'boolean' as const,
      })),
    },
    {
      title: 'Layout',
      rows: [
        {
          label: 'Recommendations Layout',
          key: 'RECOMMENDATIONS_LAYOUT',
          type: 'choice' as const,
          choices: [
            { label: 'Carousel (current)', value: 0 },
            { label: 'Masonry Grid', value: 1 },
          ],
        },
        {
          label: 'Achievements Layout',
          key: 'ACHIEVEMENTS_LAYOUT',
          type: 'choice' as const,
          choices: [
            { label: 'Sticky Card Stack (current)', value: 0 },
            { label: 'Timeline (dev branch)', value: 1 },
          ],
        },
        {
          label: 'Lazy Loading Strategy',
          key: 'LAZY_LOADING_STRATEGY',
          type: 'choice' as const,
          choices: [
            { label: 'Timer-based (lazyWithPriority)', value: 0 },
            { label: 'IntersectionObserver', value: 1 },
            { label: 'Background Preload (instant render)', value: 2 },
          ],
        },
      ],
    },
    {
      title: 'Animations',
      rows: [
        { label: 'Master Toggle', key: 'ANIMATIONS', type: 'boolean' as const },
        ...Object.entries(config.ANIM_SECTIONS || {}).map(([key]) => ({
          label: `  ${key.charAt(0) + key.slice(1).toLowerCase()}`,
          key: `ANIM_SECTIONS.${key}`,
          type: 'boolean' as const,
        })),

      ],
    },
    {
      title: 'UI Toggles',
      rows: [
        { label: 'Custom Cursor', key: 'CUSTOM_CURSOR', type: 'boolean' as const },
        { label: 'Canvas Background', key: 'CANVAS_BACKGROUND', type: 'boolean' as const },
        { label: 'GitHub Stats', key: 'GITHUB_STATS', type: 'boolean' as const },
        { label: 'Dev Comments', key: 'DEV_COMMENTS', type: 'boolean' as const },
      ],
    },
    {
      title: 'MA Logo',
      rows: [
        {
          label: 'Hover Effect',
          key: 'MA_HOVER_EFFECT',
          type: 'choice' as const,
          choices: [
            { label: 'None', value: 0 },
            { label: 'Coder Peek', value: 1 },
            { label: 'Wave Ripple', value: 2 },
          ],
        },
        {
          label: 'Dev Timer',
          key: 'DEV_MODE_TIMER',
          type: 'choice' as const,
          choices: [
            { label: '30 sec', value: 0.5 },
            { label: '1 min', value: 1 },
            { label: '5 min', value: 5 },
            { label: '10 min', value: 10 },
            { label: '15 min (default)', value: 15 },
            { label: '30 min', value: 30 },
          ],
        },
      ],
    },
  ]

  const getValue = (key: string): boolean | number => {
    const parts = key.split('.')
    if (parts.length === 2) {
      const obj = config[parts[0] as keyof DebugConfig]
      if (obj && typeof obj === 'object') return (obj as Record<string, boolean | number>)[parts[1]]
    }
    return config[key as keyof DebugConfig] as boolean | number
  }

  // WHY THREE-PHASE REORDER: (1) handleRowStart creates ordered draft copy.
  // (2) handleRowOver splices items within draft (no commits until end).
  // (3) handleRowEnd commits via setOverride. Draft state prevents partial
  // commits during drag.
  const handleRowStart = (group: string, index: number) => {
    setRowDrag({ group, index })
    const orderKeyName = group === 'Projects' ? 'PROJECTS' : 'SECTIONS' as const
    const currentOrder = config.ORDER?.[orderKeyName]
    const idMap = group === 'Projects' ? projectKeyToId : sectionKeyToId
    const keys = Object.keys(group === 'Projects' ? config.PROJECTS : config.SECTIONS)

    let orderedIds: string[]
    if (currentOrder && currentOrder.length > 0) {
      orderedIds = [...currentOrder]
      const existing = new Set(orderedIds)
      keys.forEach(k => {
        const id = idMap[k]
        if (id && !existing.has(id)) orderedIds.push(id)
      })
    } else {
      orderedIds = keys.map(k => idMap[k]).filter(Boolean) as string[]
    }

    setRowDraft((prev) => ({ ...prev, [group]: orderedIds }))
  }

  const handleRowOver = (e: React.DragEvent, group: string, index: number) => {
    e.preventDefault()
    if (!rowDrag || rowDrag.group !== group || rowDrag.index === index) return

    const draft = rowDraft[group]
    if (!draft) return

    const item = draft[rowDrag.index]
    const newOrder = draft.filter((_, i) => i !== rowDrag.index)
    newOrder.splice(index, 0, item)

    setRowDraft((prev) => ({ ...prev, [group]: newOrder }))
    setRowDrag({ group, index })
  }

  const handleRowEnd = () => {
    Object.entries(rowDraft).forEach(([group, ids]) => {
      const orderKey = group === 'Projects' ? 'PROJECTS' : 'SECTIONS'
      setOverride(`ORDER.${orderKey}`, ids)
    })
    setRowDrag(null)
    setRowDraft({})
    setTouchDrag(null)
  }

  const handleTouchStart = (group: string, index: number) => {
    handleRowStart(group, index)
    setTouchDrag({ group, index })
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDrag) return
    const touch = e.touches[0]
    const el = document.elementFromPoint(touch.clientX, touch.clientY)
    if (!el) return
    const row = (el as Element).closest('[data-group]')
    if (!row) return
    const targetGroup = row.getAttribute('data-group') ?? ''
    const targetIndex = parseInt(row.getAttribute('data-index') ?? '', 10)
    if (isNaN(targetIndex) || targetGroup !== touchDrag.group || targetIndex === touchDrag.index) return
    const draft = rowDraft[targetGroup]
    if (!draft) return
    const item = draft[touchDrag.index]
    const newOrder = draft.filter((_, i) => i !== touchDrag.index)
    newOrder.splice(targetIndex, 0, item)
    setRowDraft((prev) => ({ ...prev, [targetGroup]: newOrder }))
    setTouchDrag({ group: targetGroup, index: targetIndex })
  }

  const handleTouchEnd = () => {
    handleRowEnd()
  }

  return (
    <>
      <button
        type="button"
        className={`debug-fab ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((p) => !p)}
        aria-label="Toggle debug panel"
        title="Debug Panel"
      >
        <span className="debug-fab-icon">{open ? '\u2715' : '\u2699'}</span>
      </button>

      <div className={`debug-panel ${open ? 'is-visible' : ''}`}>
        <div className="debug-panel-header">
          <h3>Debug Panel</h3>
          <span className="debug-badge">dev mode</span>
        </div>

        <div className="debug-panel-body">
          <div className="debug-prod-toggle">
            <label className="debug-prod-label">
              <span>Apply to Production Config</span>
              <span className="debug-prod-hint">{productionMode ? 'ON: "Apply to Source" will write to source files' : 'OFF: changes saved to localStorage only'}</span>
            </label>
            <button
              type="button"
              className={`debug-toggle ${productionMode ? 'is-on' : 'is-off'}`}
              onClick={() => setProductionMode(!productionMode)}
            >
              {productionMode ? 'ON' : 'OFF'}
            </button>
          </div>

          <div className="debug-search-wrap">
            <input
              type="text"
              className="debug-search-input"
              placeholder="Filter toggles..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              aria-label="Filter debug toggles"
            />
            {filterText && (
              <button
                type="button"
                className="debug-search-clear"
                onClick={() => setFilterText('')}
                aria-label="Clear filter"
              >
                ✕
              </button>
            )}
          </div>

          {groups.map((group) => (
            <div key={group.title} className="debug-group">
              <div className="debug-group-header">
                <h4 className="debug-group-title">{group.title}</h4>
                <span className="debug-priority-hint" style={{ fontSize: '0.6rem', color: 'var(--text-secondary)', marginRight: '0.5rem' }}>
                  {['Projects', 'Sections'].includes(group.title) ? '☰ = order, ⚡ = priority' : ''}
                </span>
                {['Projects', 'Sections'].includes(group.title) && (
                  <>
                    <button
                      type="button"
                      className="debug-toggle-all"
                      onClick={() => {
                        const orderKey = group.title === 'Projects' ? 'PROJECTS' : 'SECTIONS'
                        setOverride(`ORDER.${orderKey}`, null)
                      }}
                      style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', width: 'auto', color: '#ff4444', borderColor: 'rgba(255,68,68,0.3)', background: 'transparent', border: '1px solid', borderRadius: '4px', cursor: 'pointer', marginRight: '0.25rem' }}
                      title="Reset display order to default"
                    >
                      Reset Order
                    </button>
                    <button
                      type="button"
                      className="debug-toggle-all"
                      onClick={() => {
                        const entries = Object.entries(
                          group.title === 'Projects' ? config.PROJECTS : config.SECTIONS
                        )
                        const allOn = entries.every(([, v]) => v)
                        entries.forEach(([key]) => {
                          setOverride(`${group.title === 'Projects' ? 'PROJECTS' : 'SECTIONS'}.${key}`, !allOn)
                        })
                      }}
                    >
                      {Object.values(group.title === 'Projects' ? config.PROJECTS : config.SECTIONS).every(Boolean) ? 'All OFF' : 'All ON'}
                    </button>
                  </>
                )}
              </div>
              <div className="debug-group-items">
                {(filterText
                  ? group.rows.filter((r) =>
                      r.label.toLowerCase().includes(filterText.toLowerCase()) ||
                      r.key.toLowerCase().includes(filterText.toLowerCase())
                    )
                  : group.rows
                ).map((row, rowIndex) => {
                  const info = DEBUG_INFO[row.key]
                  const currentVal = getValue(row.key)
                  const isDragGroup = group.title === 'Projects' || group.title === 'Sections'
                  return (
                    <div
                      key={row.key}
                      className={`debug-row ${group.title === 'Animations' && row.key.startsWith('ANIM_SECTIONS.') ? (currentVal ? 'anim-on' : 'anim-off') : ''} ${touchDrag?.group === group.title && touchDrag?.index === rowIndex ? 'debug-row-dragging' : ''}`}
                      data-group={isDragGroup ? group.title : undefined}
                      data-index={isDragGroup ? rowIndex : undefined}
                      draggable={isDragGroup}
                      onDragStart={isDragGroup ? () => handleRowStart(group.title, rowIndex) : undefined}
                      onDragOver={isDragGroup ? (e) => handleRowOver(e, group.title, rowIndex) : undefined}
                      onDragEnd={isDragGroup ? handleRowEnd : undefined}
                      onTouchStart={isDragGroup ? () => handleTouchStart(group.title, rowIndex) : undefined}
                      onTouchMove={isDragGroup ? handleTouchMove : undefined}
                      onTouchEnd={isDragGroup ? handleTouchEnd : undefined}
                    >
                      {isDragGroup && <span className="debug-order-grip" style={{ cursor: 'grab' }}>☰</span>}
                      <div className="debug-label-wrap">
                        <label className="debug-label" title={info?.description || row.label}>{row.label}</label>
                        {info && !(group.title === 'Animations' && row.key.startsWith('ANIM_SECTIONS.')) && (
                          <div className="debug-info-wrap">
                            <button
                              type="button"
                              className="debug-info-btn"
                              onClick={(e) => {
                                if (tooltipKey === row.key) {
                                  setTooltipKey(null)
                                  setTooltipPos(null)
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect()
                                  let left = rect.left
                                  const tooltipWidth = 260
                                  if (left + tooltipWidth > window.innerWidth - 16) {
                                    left = window.innerWidth - tooltipWidth - 16
                                  }
                                  if (left < 8) left = 8
                                  setTooltipPos({ top: rect.bottom + 8, left, key: row.key })
                                  setTooltipKey(row.key)
                                }
                              }}
                              aria-label="Toggle info"
                              title={info.description}
                            >
                              ?
                            </button>
                          </div>
                        )}
                      </div>
                      {isDragGroup && (
                        <button
                          type="button"
                          className={`debug-priority-btn ${(getValue(`PRIORITY.${row.key.split('.')[1]}`) as number) < 99 ? 'is-priority' : ''}`}
                          onClick={() => {
                            const k = row.key.split('.')[1]
                            const cur = getValue(`PRIORITY.${k}`) as number
                            setOverride(`PRIORITY.${k}`, cur < 99 ? 99 : 0)
                          }}
                          title={(getValue(`PRIORITY.${row.key.split('.')[1]}`) as number) < 99 ? 'Priority ON - eager load (click to disable)' : 'Priority OFF - lazy load (click to enable)'}
                        >
                          ⚡
                        </button>
                      )}
                      {group.title === 'Animations' && row.key.startsWith('ANIM_SECTIONS.') ? (
                        <div className="debug-anim-controls debug-anim-controls--inline">
                          {!!currentVal && (() => {
                            const section = row.key.split('.')[1]
                            const speedKey = `ANIM_SPEED.${section}`
                            const typeKey = `ANIM_TYPE.${section}`
                            const currentSpeed = getValue(speedKey) as number
                            const currentType = `${getValue(typeKey)}`
                            const springVal = section === 'EXPERIENCE' ? getValue('EXPERIENCE_SPRING_ANIM') : false
                            return (
                              <div className="debug-anim-stacked">
                                <div className="debug-anim-stacked-top">
                                  {section === 'EXPERIENCE' && (
                                    <button
                                      type="button"
                                      className={`debug-toggle debug-toggle--xxs ${springVal ? 'is-on' : 'is-off'}`}
                                      onClick={() => setOverride('EXPERIENCE_SPRING_ANIM', !springVal)}
                                      title={springVal ? 'Spring ON - bouncy entrance' : 'Spring OFF - tween easeOut'}
                                    >
                                      S
                                    </button>
                                  )}
                                  <select
                                    className="debug-anim-type-select"
                                    value={currentType}
                                    onChange={(e) => setOverride(typeKey, e.target.value as never)}
                                  >
                                    <option value="fadeInUp">Fade Up</option>
                                    <option value="fadeIn">Fade In</option>
                                    <option value="scaleIn">Scale In</option>
                                    <option value="none">None</option>
                                  </select>
                                </div>
                                <div className="debug-anim-stacked-bottom">
                                  <div className="debug-anim-speed-wrap">
                                    <input
                                      type="range"
                                      className="debug-anim-speed-slider"
                                      min="0.2"
                                      max="2.0"
                                      step="0.1"
                                      value={currentSpeed}
                                      onChange={(e) => setOverride(speedKey, parseFloat(e.target.value))}
                                    />
                                    <span className="debug-anim-speed-label">{currentSpeed.toFixed(1)}s</span>
                                  </div>
                                </div>
                              </div>
                            )
                          })()}
                          {info && (
                            <div style={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <button
                                type="button"
                                className="debug-info-btn"
                                onClick={(e) => {
                                  if (tooltipKey === row.key) {
                                    setTooltipKey(null)
                                    setTooltipPos(null)
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    let left = rect.left
                                    const tooltipWidth = 260
                                    if (left + tooltipWidth > window.innerWidth - 16) {
                                      left = window.innerWidth - tooltipWidth - 16
                                    }
                                    if (left < 8) left = 8
                                    setTooltipPos({ top: rect.bottom + 8, left, key: row.key })
                                    setTooltipKey(row.key)
                                  }
                                }}
                                aria-label="Toggle info"
                                title={info.description}
                              >
                                ?
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            className={`debug-toggle ${currentVal ? 'is-on' : 'is-off'}`}
                            onClick={() => setOverride(row.key, !currentVal)}
                          >
                            {currentVal ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      ) : row.type === 'boolean' ? (
                        <button
                          type="button"
                          className={`debug-toggle ${currentVal ? 'is-on' : 'is-off'}`}
                          onClick={() => setOverride(row.key, !currentVal)}
                        >
                          {currentVal ? 'ON' : 'OFF'}
                        </button>
                      ) : (
                        <select
                          className="debug-select"
                          value={currentVal as number}
                          onChange={(e) => setOverride(row.key, Number(e.target.value))}
                        >
                          {(row.choices || []).map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

        </div>

        <div className="debug-panel-footer">
          <div className="debug-actions">
            <button
              type="button"
              className={`debug-btn debug-btn-reset ${confirmReset ? 'debug-btn-confirm' : ''}`}
              onClick={() => {
                if (confirmReset) {
                  resetAll()
                  setConfirmReset(false)
                } else {
                  setConfirmReset(true)
                  setTimeout(() => setConfirmReset(false), 3000)
                }
              }}
              title="Reset all debug overrides to production defaults (in-memory only). Click again to confirm."
            >
              {confirmReset ? 'Confirm Reset?' : 'Reset All'}
            </button>
            <button
              type="button"
              className="debug-btn"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(JSON.stringify(config, null, 2))
                } catch { /* silent */ }
              }}
              title="Copy current debug config to clipboard"
            >
              Export
            </button>
            <button
              type="button"
              className="debug-btn"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText()
                  const parsed = JSON.parse(text)
                  if (parsed && typeof parsed === 'object') {
                    commitOverrides()
                    Object.entries(parsed).forEach(([key, value]) => {
                      setOverride(key, value as never)
                    })
                  }
                } catch { /* silent */ }
              }}
              title="Import debug config from clipboard (overwrites current settings)"
            >
              Import
            </button>
            {/* WHY AI-ASSISTED WORKFLOW: Copies current config JSON to clipboard with
            instruction to tell AI "finalize production config". This is an AI-assisted
            config persistence workflow: the AI writes the config to the source file
            on the user's request. */}
            <button
              type="button"
              className={`debug-btn ${productionMode ? 'debug-btn-apply-source' : 'debug-btn-commit'}`}
              onClick={() => {
                commitOverrides()
                if (productionMode) {
                  try { localStorage.setItem(APPLY_PENDING_KEY, '1') } catch { /* silent */ }
                  try { navigator.clipboard.writeText(JSON.stringify(config, null, 2)) } catch { /* silent */ }
                } else {
                  try { localStorage.removeItem(APPLY_PENDING_KEY) } catch { /* silent */ }
                }
                setCommitted(true)
                setTimeout(() => setCommitted(false), 3000)
              }}
              title={productionMode
                ? 'Copies current config to clipboard. Tell the AI "finalize production config" to write to source files.'
                : 'Save current debug overrides to localStorage so they survive page reload'}
            >
              {committed
                ? (productionMode ? 'Copied! Tell AI to finalize.' : 'Committed!')
                : (productionMode ? 'Apply to Source' : 'Commit Changes')}
            </button>
          </div>
        </div>
      </div>

      {open && <div className="debug-backdrop" onClick={() => setOpen(false)} />}

      {tooltipKey && tooltipPos && (() => {
        const info = DEBUG_INFO[tooltipKey]
        if (!info) return null
        return (
          <div
            className="debug-tooltip"
            style={{
              position: 'fixed',
              top: tooltipPos.top,
              left: tooltipPos.left,
              zIndex: 100000,
            }}
          >
            <div className="debug-tooltip-desc">{info.description}</div>
            <div className="debug-tooltip-row">
              <span className="debug-tooltip-label">Performance:</span>
              <span className={`debug-tooltip-val impact-${info.performanceImpact.toLowerCase()}`}>
                {info.performanceImpact}
              </span>
            </div>
            <div className="debug-tooltip-row">
              <span className="debug-tooltip-label">UX Tradeoff:</span>
              <span className="debug-tooltip-val">{info.uxTradeoff}</span>
            </div>
            <div className="debug-tooltip-row">
              <span className="debug-tooltip-label">Recommended:</span>
              <span className="debug-tooltip-val">{info.recommended.toString()}</span>
            </div>
            <div className="debug-tooltip-row">
              <span className="debug-tooltip-label">Gain:</span>
              <span className="debug-tooltip-val">{info.gain}</span>
            </div>
            <div className="debug-tooltip-row">
              <span className="debug-tooltip-label">Lose:</span>
              <span className="debug-tooltip-val">{info.lose}</span>
            </div>
          </div>
        )
      })()}
    </>
  )
}

export default DebugPanel
